import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as settingsApi from '../../api/settingsApi';
import { DatabaseSettings } from '../../api/settingsApi';

// Types
export interface SettingsState {
  settings: DatabaseSettings | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: SettingsState = {
  settings: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchSettings = createAsyncThunk(
  'settings/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsApi.getSettings();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch settings');
    }
  }
);

export const updateSettings = createAsyncThunk(
  'settings/update',
  async (settings: DatabaseSettings, { rejectWithValue }) => {
    try {
      const response = await settingsApi.updateSettings(settings);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update settings');
    }
  }
);

// Slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch settings
    builder.addCase(fetchSettings.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchSettings.fulfilled,
      (state, action: PayloadAction<DatabaseSettings>) => {
        state.loading = false;
        state.settings = action.payload;
      }
    );
    builder.addCase(fetchSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update settings
    builder.addCase(updateSettings.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      updateSettings.fulfilled,
      (state, action: PayloadAction<DatabaseSettings>) => {
        state.loading = false;
        state.settings = action.payload;
      }
    );
    builder.addCase(updateSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearSettingsError } = settingsSlice.actions;

export default settingsSlice.reducer;